// React hooks and part code management service
import { useState, useEffect } from "react";
import partCodesService from "services/part-code-service";
import partCategoryService from "services/part-category-service";
import formatPartCode from "helper/formatPartCode";

// MUI and Material Dashboard components
import Autocomplete from "@mui/material/Autocomplete";
import Card from "@mui/material/Card";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import FormField from "layouts/applications/wizard/components/FormField";
import CircularProgress from '@mui//material/CircularProgress'

export default function SelectPartCode({
    changeHandler,
    setSelectedPartCode,
    error,
    editMode
}) {
    const [parentCategories, setParentCategories] = useState([]); // Stores fetched Parent Categories
    const [selectedParentCategory, setSelectedParentCategory] = useState(""); // Stores the selected parentCategory
    const [partCategories, setPartCategories] = useState([]); // Stores fetched Part Categories
    const [selectedPartCategory, setSelectedPartCategory] = useState(""); // Stores the selected partCategory
    const [filteredPartCategories, setFilteredPartCategories] = useState([]); // Stores the part categories filtered by parent category
    const [partCodes, setPartCodes] = useState([]); // Stores fetched Part Codes
    const [fileteredPartCodes, setFilteredPartCodes] = useState([]); // Stores part codes filtered by part category
    const [localSelectedPartCode, setLocalSelectedPartCode] = useState(""); // Stores locally the selected part code
    const [isLoading, setIsLoading] = useState(false); // Loading state

    // Fetches the parent categories, part categories and part codes
    useEffect(() => {
        (async () => {
            setIsLoading(true);
            const response1 = await partCategoryService.getAllParentCategories();
            const response2 = await partCategoryService.getAllPartCategories();
            const response3 = await partCodesService.getParts();
            setParentCategories(response1.data.attributes);
            const approvedPartCodes = filterByStatus(response3.data.attributes);
            setPartCodes(approvedPartCodes);
            setPartCategories(response2.data.attributes);
        })();
    }, []);

    // Handles Category Change
    useEffect(() => {
        if (selectedParentCategory) {
            const filterpartcategories = partCategories.filter((partCategory) => partCategory.parentCategory === selectedParentCategory);
            setFilteredPartCategories(filterpartcategories);
        } else {
            // Empties the fields related to parentCategory if no parentCategory is selected
            setFilteredPartCategories([]);
        }
        if (partCodes.length > 0) {
            setIsLoading(false);
        }
        setSelectedPartCategory("");
    }, [selectedParentCategory, partCodes]);

    // Handles Part Code change
    useEffect(() => {
        if (selectedPartCategory) {
            const filterparts = partCodes.filter((part) => part.partCategory.categoryCode === selectedPartCategory.categoryCode);
            const formattedPartCodes = filterparts.map((part) => ({
                ...part,
                partCode: formatPartCode(part)
            }));
            setFilteredPartCodes(formattedPartCodes);
        } else {
            // Empties the fields related to partCategory if no partCategory is selected
            setFilteredPartCodes([]);
        }
        setSelectedPartCode("");
        setLocalSelectedPartCode("");
    }, [selectedPartCategory]);

    const filterByStatus = (partCodes) => {
        return partCodes.filter(partCode => partCode.status === "Approved");
    }

    // Reflects part code change locally and in the parent component
    const changeSelectedPartCode = (partCode) => {
        if (!partCode) {
            setLocalSelectedPartCode("");
            setSelectedPartCode("");
            changeHandler("identifier", "");
            return;
        }
        setLocalSelectedPartCode({
            ...partCode,
            partCode: formatPartCode(partCode)
        });
        setSelectedPartCode(partCode);
        changeHandler("identifier", formatPartCode(partCode));
    };



    const commonProps = { // Props common for the input fields
        display: "flex",
        flexDirection: "column",
        fullWidth: true,
        marginBottom: "1rem",
        marginTop: "1rem"
    };
    return (

        <Card>
            {isLoading ? (
                <MDBox margin={2} p={3} display="flex" justifyContent="center" alignItems="center">
                    <CircularProgress />
                </MDBox>
            ) : (
                <MDBox margin={2} p={3}>
                    <MDTypography variant="h4" fontWeight="bold">
                        {editMode ? ("Select a New Part Code") : ("Select a Part Code")}
                    </MDTypography>

                    {/* Parent Category */}

                    <MDBox {...commonProps}>
                        <Autocomplete
                            defaultValue=""
                            options={parentCategories}
                            getOptionLabel={(option) => option}
                            value={selectedParentCategory ?? ""}
                            onChange={(event, newValue) => {
                                setSelectedParentCategory(newValue);
                                setLocalSelectedPartCode(null);   
                                setSelectedPartCode("");
                            }}
                            renderInput={(params) => (
                                <FormField {...params} label="Parent Category" InputLabelProps={{ shrink: true }} />
                            )} />
                    </MDBox>


                    {/* Part Category*/}

                    <MDBox {...commonProps}>
                        <Autocomplete
                            defaultValue=""
                            options={filteredPartCategories}
                            getOptionLabel={(option) => (option ? option.categoryCode : "")}
                            value={selectedPartCategory ?? ""}
                            onChange={(event, newValue) => {
                                setSelectedPartCategory(newValue);
                            }}
                            renderInput={(params) => (
                                <FormField {...params} label="Part Category" InputLabelProps={{ shrink: true }} />
                            )} />
                    </MDBox>

                    {/* Part Code */}
                    <MDBox {...commonProps}>
                        <Autocomplete
                            defaultValue=""
                            options={fileteredPartCodes}
                            getOptionLabel={(option) => (option ? option.partCode : "")}
                            value={localSelectedPartCode ?? ""}
                            error={error && error.partCode}
                            onChange={(event, newValue) => {
                                changeSelectedPartCode(newValue);
                            }}
                            isOptionEqualToValue={(option, value) => option.unique_num === value.unique_num}
                            renderInput={(params) => (
                                <FormField {...params} label="Part Code" InputLabelProps={{ shrink: true }} />
                            )} />
                    </MDBox>

                    <MDBox {...commonProps}>
                        {error && error.partCode && (
                            <MDTypography variant="caption" color="error">
                                Selecting a Part Code is required.
                            </MDTypography>
                        )}
                    </MDBox>
                </MDBox>)}
        </Card>


    )
}